import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Switch } from 'components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Badge } from 'components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from 'components/ui/dialog';
import { useAuth } from 'contexts/AuthContext';
import { useLanguage } from 'lib/i18n';
import api, { getApiErrorMessage } from 'lib/api';
import { toast } from 'sonner';
import { BadgeCheck, Star, Upload, Trash2, Download, AlertTriangle, Ban, Heart, Bell } from 'lucide-react';
import { isPushSupported, getPushSubscription, subscribeToPush, unsubscribeFromPush } from 'lib/push';

const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const MAX_DIMENSION = 900;
const MAX_AVATAR_DATA_URL_LENGTH = 300000;

const loadImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('IMAGE_DECODE_ERROR'));
    img.src = String(reader.result || '');
  };
  reader.onerror = () => reject(new Error('FILE_READ_ERROR'));
  reader.readAsDataURL(file);
});

const compressAvatarImage = async (file) => {
  const img = await loadImage(file);
  let ratio = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('CANVAS_UNAVAILABLE');

  for (let pass = 0; pass < 5; pass += 1) {
    const width = Math.max(1, Math.round(img.width * ratio));
    const height = Math.max(1, Math.round(img.height * ratio));
    canvas.width = width;
    canvas.height = height;
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    let quality = 0.84;
    while (quality >= 0.45) {
      const output = canvas.toDataURL('image/jpeg', quality);
      if (output.length <= MAX_AVATAR_DATA_URL_LENGTH) {
        return output;
      }
      quality -= 0.08;
    }

    ratio *= 0.8;
  }

  throw new Error('IMAGE_TOO_LARGE');
};

export default function ProfilePage() {
  const { user, refreshUser, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [roles, setRoles] = useState(user.roles || ['sender']);
  const [rolesSaving, setRolesSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: user.full_name || '', phone: user.phone || '',
    address: user.address || '', city: user.city || '', country: user.country || '',
    bio: user.bio || '',
    avatar_url: user.avatar_url || '',
    marketing_email_opt_in: !!user.marketing_email_opt_in,
    notification_prefs: user.notification_prefs || { email: true, in_app: true },
  });
  const [avatarPreview, setAvatarPreview] = useState(user.avatar_url || '');
  const [avatarProcessing, setAvatarProcessing] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [favoriteDeliverers, setFavoriteDeliverers] = useState([]);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    setRoles(user.roles || ['sender']);
  }, [user.id, user.roles]);

  useEffect(() => {
    if (location.hash !== '#roles') return;
    const timer = window.setTimeout(() => document.getElementById('roles')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  const toggleRole = (role) => {
    setRoles(current => {
      if (current.includes(role)) {
        if (current.length === 1) {
          toast.error(t('profile.roleRequired'));
          return current;
        }
        return current.filter(item => item !== role);
      }
      return [...current, role];
    });
  };

  const saveRoles = async () => {
    if (!roles.length || rolesSaving) return;
    setRolesSaving(true);
    try {
      const response = await api.put('/users/me', { roles });
      const savedRoles = response.data?.roles || roles;
      setRoles(savedRoles);
      const activeRole = localStorage.getItem('blablacolis_active_role');
      if (activeRole && !savedRoles.includes(activeRole)) {
        localStorage.setItem('blablacolis_active_role', savedRoles[0]);
      }
      await refreshUser();
      toast.success(t('profile.rolesUpdated'));
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('profile.rolesUpdateError')));
    } finally {
      setRolesSaving(false);
    }
  };

  useEffect(() => {
    if (isPushSupported()) getPushSubscription().then((sub) => setPushEnabled(!!sub));
  }, []);

  const togglePush = async (enabled) => {
    setPushBusy(true);
    try {
      if (enabled) {
        await subscribeToPush();
        toast.success(t('profile.pushOn'));
      } else {
        await unsubscribeFromPush();
        toast.success(t('profile.pushOff'));
      }
      setPushEnabled(enabled);
    } catch (err) {
      toast.error(err?.message || t('profile.actionImpossible'));
    } finally { setPushBusy(false); }
  };

  useEffect(() => {
    api.get('/users/me/blocked').then(r => setBlockedUsers(r.data)).catch(() => {});
    api.get('/users/me/favorites').then(r => setFavoriteDeliverers(r.data)).catch(() => {});
  }, []);

  const unblockUser = async (userId) => {
    try {
      await api.post(`/users/${userId}/unblock`);
      setBlockedUsers((prev) => prev.filter((u) => u.id !== userId));
      refreshUser();
      toast.success(t('profile.unblocked'));
    } catch (err) { toast.error(getApiErrorMessage(err, t('profile.actionImpossible'))); }
  };

  const removeFavorite = async (userId) => {
    try {
      await api.post(`/users/${userId}/unfavorite`);
      setFavoriteDeliverers((prev) => prev.filter((u) => u.id !== userId));
      refreshUser();
      toast.success(t('profile.removedFavorite'));
    } catch (err) { toast.error(getApiErrorMessage(err, t('profile.actionImpossible'))); }
  };

  const handleAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('profile.selectImage'));
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(t('profile.imageTooLarge'));
      e.target.value = '';
      return;
    }

    setAvatarProcessing(true);
    try {
      const dataUrl = await compressAvatarImage(file);
      set('avatar_url', dataUrl);
      setAvatarPreview(dataUrl);
      toast.success(t('profile.imageReady'));
    } catch {
      toast.error(t('profile.imageCompressError'));
    } finally {
      setAvatarProcessing(false);
      e.target.value = '';
    }
  };

  const clearAvatar = () => {
    set('avatar_url', '');
    setAvatarPreview('');
  };

  const save = async () => {
    try {
      await api.put('/users/me', form);
      toast.success(t('profile.updated'));
      refreshUser();
    } catch (err) { toast.error(getApiErrorMessage(err, t('profile.saveError'))); }
  };

  const exportMyData = async () => {
    try {
      const response = await api.get('/users/me/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'colibuddy-mes-donnees.json';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(getApiErrorMessage(err, t('profile.exportError'))); }
  };

  const deleteAccount = async () => {
    if (!deletePassword) return;
    setDeleteBusy(true);
    try {
      await api.delete('/users/me', { data: { password: deletePassword } });
      toast.success(t('profile.accountDeleted'));
      logout();
      navigate('/');
    } catch (err) { toast.error(getApiErrorMessage(err, t('profile.deleteError'))); }
    finally { setDeleteBusy(false); }
  };

  return (
    <DashboardShell>
      <h1 className="text-2xl sm:text-3xl font-bold mb-6">{t('profile.title')}</h1>
      <div className="grid lg:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-5 text-center">
            <Avatar className="h-20 w-20 mx-auto mb-3">
              <AvatarImage src={avatarPreview || user.avatar_url} />
              <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                {(user.full_name || 'U').split(' ').map(n => n[0]).slice(0,2).join('')}
              </AvatarFallback>
            </Avatar>
            <div className="flex items-center justify-center gap-2 mb-3">
              <Label htmlFor="profile-avatar-upload" className="cursor-pointer inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border hover:bg-secondary">
                <Upload size={12} />
                {avatarProcessing ? t('profile.avatarOptimizing') : t('profile.avatarChange')}
              </Label>
              <Input
                id="profile-avatar-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarFile}
                disabled={avatarProcessing}
                data-testid="profile-avatar-input"
              />
              {(avatarPreview || user.avatar_url) && (
                <Button type="button" size="sm" variant="outline" onClick={clearAvatar} disabled={avatarProcessing}>
                  <Trash2 size={12} className="mr-1" /> {t('profile.avatarRemove')}
                </Button>
              )}
            </div>
            <h2 className="font-semibold text-lg">{user.full_name}</h2>
            <p className="text-sm text-muted-foreground">{user.email}</p>
            <div className="flex flex-wrap justify-center gap-1 mt-3">
              {(user.roles || []).map(r => <Badge key={r} variant="outline">{t(`role.${r}`)}</Badge>)}
              {user.is_admin && <Badge>Admin</Badge>}
            </div>
            {user.rating > 0 && (
              <div className="mt-3 flex items-center justify-center gap-1 text-sm">
                <Star size={14} className="fill-amber-500 text-amber-500" />
                <span className="font-medium">{user.rating.toFixed(1)}</span>
                <span className="text-muted-foreground">({user.review_count} avis)</span>
              </div>
            )}
            <div className="mt-4 space-y-1 text-sm">
              {user.is_verified_email && <div className="flex items-center justify-center gap-1 text-emerald-700"><BadgeCheck size={14}/> {t('profile.verifiedEmail')}</div>}
              {user.deliverer_validated && <div className="flex items-center justify-center gap-1 text-emerald-700"><BadgeCheck size={14}/> {t('profile.verifiedDeliverer')}</div>}
            </div>
          </CardContent>
        </Card>
        <div className="lg:col-span-2 space-y-4">
          <Card id="roles" data-testid="profile-roles-card">
            <CardHeader>
              <CardTitle className="text-base">{t('profile.manageRoles')}</CardTitle>
              <p className="text-sm text-muted-foreground">{t('profile.rolesDescription')}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2 sm:grid-cols-3">
                {['sender', 'traveler', 'deliverer'].map(role => {
                  const selected = roles.includes(role);
                  return (
                    <button
                      key={role}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => toggleRole(role)}
                      data-testid={`profile-role-${role}`}
                      className={`rounded-lg border p-3 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${selected ? 'border-primary bg-primary/5' : 'hover:bg-secondary'}`}
                    >
                      <span className="flex items-center justify-between font-medium">
                        {t(`role.${role}`)}
                        <span className={`rounded-full px-2 py-0.5 text-xs ${selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                          {selected ? t('profile.roleSelected') : t('profile.roleNotSelected')}
                        </span>
                      </span>
                      <span className="mt-1 block text-xs text-muted-foreground">{t(`profile.roleDescription.${role}`)}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">{t('profile.rolesSafetyNote')}</p>
              <Button type="button" onClick={saveRoles} disabled={rolesSaving || roles.length === 0 || JSON.stringify([...roles].sort()) === JSON.stringify([...(user.roles || [])].sort())} data-testid="profile-save-roles-button">
                {rolesSaving ? t('profile.rolesSaving') : t('profile.saveRoles')}
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">{t('profile.information')}</CardTitle></CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-3">
              <div><Label>{t('profile.fullName')}</Label><Input value={form.full_name} onChange={e => set('full_name', e.target.value)} data-testid="profile-name-input" /></div>
              <div><Label>{t('profile.phone')}</Label><Input value={form.phone} onChange={e => set('phone', e.target.value)} data-testid="profile-phone-input" /></div>
              <div><Label>{t('profile.city')}</Label><Input value={form.city} onChange={e => set('city', e.target.value)} /></div>
              <div><Label>{t('profile.country')}</Label><Input value={form.country} onChange={e => set('country', e.target.value)} /></div>
              <div className="sm:col-span-2"><Label>{t('profile.address')}</Label><Input value={form.address} onChange={e => set('address', e.target.value)} /></div>
              <div className="sm:col-span-2"><Label>{t('profile.bio')}</Label><Textarea value={form.bio} onChange={e => set('bio', e.target.value)} placeholder={t('profile.bioPlaceholder')} /></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">{t('profile.languageTitle')}</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">{t('profile.languageDescription')}</p>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:max-w-xs"
                aria-label={t('language.label')}
                value={language}
                onChange={(event) => setLanguage(event.target.value).then(() => toast.success(t('profile.updated'))).catch(() => toast.error(t('profile.languageSaveError')))}
              >
                <option value="fr">{t('language.french')}</option>
                <option value="en">{t('language.english')}</option>
              </select>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">{t('profile.notifications')}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>{t('profile.emailNotifications')}</Label>
                <Switch checked={form.notification_prefs.email} onCheckedChange={v => set('notification_prefs', { ...form.notification_prefs, email: v })} />
              </div>
              <div className="flex items-center justify-between gap-4">
                <Label htmlFor="marketing-email-opt-in">{t('profile.marketingEmails')}</Label>
                <Switch id="marketing-email-opt-in" checked={form.marketing_email_opt_in} onCheckedChange={value => set('marketing_email_opt_in', value)} data-testid="marketing-email-opt-in" />
              </div>
              <p className="text-xs text-muted-foreground">{t('profile.marketingEmailsNote')}</p>
              <div className="flex items-center justify-between">
                <Label>{t('profile.inAppNotifications')}</Label>
                <Switch checked={form.notification_prefs.in_app} onCheckedChange={v => set('notification_prefs', { ...form.notification_prefs, in_app: v })} />
              </div>
              {isPushSupported() && (
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-1"><Bell size={14} /> {t('profile.pushNotifications')}</Label>
                  <Switch checked={pushEnabled} disabled={pushBusy} onCheckedChange={togglePush} />
                </div>
              )}
            </CardContent>
          </Card>
          {favoriteDeliverers.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Mes livreurs favoris</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {favoriteDeliverers.map((u) => (
                  <div key={u.id} className="flex items-center justify-between gap-3 border-b last:border-0 pb-2 last:pb-0">
                    <Link to={`/users/${u.id}`} className="text-sm font-medium hover:underline">{u.full_name}</Link>
                    <Button size="sm" variant="ghost" onClick={() => removeFavorite(u.id)}><Heart size={14} className="mr-1 fill-current" /> {t('profile.remove')}</Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {blockedUsers.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Utilisateurs bloqués</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {blockedUsers.map((u) => (
                  <div key={u.id} className="flex items-center justify-between gap-3 border-b last:border-0 pb-2 last:pb-0">
                    <span className="text-sm font-medium">{u.full_name}</span>
                    <Button size="sm" variant="outline" onClick={() => unblockUser(u.id)}><Ban size={14} className="mr-1" /> Débloquer</Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader><CardTitle className="text-base">Confidentialité et données</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">{t('profile.exportTitle')}</p>
                  <p className="text-xs text-muted-foreground">{t('profile.exportDescription')}</p>
                </div>
                <Button size="sm" variant="outline" onClick={exportMyData} data-testid="export-data-button"><Download size={14} className="mr-1" /> {t('profile.exportButton')}</Button>
              </div>
              <div className="flex items-center justify-between gap-3 pt-3 border-t">
                <div>
                  <p className="text-sm font-medium text-destructive">{t('profile.deleteTitle')}</p>
                  <p className="text-xs text-muted-foreground">{t('profile.deleteDescription')}</p>
                </div>
                <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="destructive" data-testid="delete-account-button"><Trash2 size={14} className="mr-1" /> {t('profile.deleteTitle')}</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle size={16} className="text-destructive" /> {t('profile.deleteConfirm')}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">{t('profile.deleteWarning')}</p>
                      <div><Label>{t('profile.password')}</Label><Input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)} data-testid="delete-account-password-input" /></div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setDeleteOpen(false)}>{t('profile.cancel')}</Button>
                      <Button variant="destructive" onClick={deleteAccount} disabled={deleteBusy || !deletePassword} data-testid="confirm-delete-account-button">{t('profile.deleteForever')}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
          <div className="flex justify-end"><Button onClick={save} disabled={avatarProcessing} data-testid="profile-save-button">{t('profile.save')}</Button></div>
        </div>
      </div>
    </DashboardShell>
  );
}
